<template>
  <form ref="registrationForm" class="w-full" :action="panelLinks.registerUrl.value" method="post" @submit="(e) => { e.preventDefault(); dispatchPost(e); return false; }">
    <div class="text-xl">
      <div class="mt-4">
        <label for="name" class="block font-medium text-sm text-mars-dk pl-2">
          <span>{{ $t('landing.form.name') + '*' }}</span> 
        </label>
        <input id="name" ref="nameInput" v-model="form.name" name="name" type="input" class="form-input rounded-md shadow-sm mt-1 block w-full" required autocomplete="name">
      </div>
      <!-- Country -->
      <!--TODO: transform this block into a component with an easier API? -->
      <ClientOnly> <!-- hydration issues -->
        <Combobox v-model="form.country" name="country" as="div" class="mt-4" @update:model-value="query = ''">
          <ComboboxLabel class="block text-sm font-medium leading-6 text-gray-900">{{ $t('landing.form.country') + '*' }}</ComboboxLabel>
          <div class="relative mt-2">
            <ComboboxInput class="form-input rounded-md shadow-sm mt-1 block w-full" :display-value="(item) => (item as Country)?.label" autocomplete="country" @change="query = $event.target.value" @blur="query = ''"/>
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
              <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>

            <ComboboxOptions v-if="filteredCountries.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              <ComboboxOption v-for="country in filteredCountries" :key="country.code" v-slot="{ active, selected }" :value="country" as="template">
                <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-highlight text-white' : 'text-mars-dk']">
                  <span :class="['block truncate', selected && 'font-semibold']">
                    {{ country.label }}
                  </span>

                  <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-highlight']">
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </div>
        </Combobox>
      </ClientOnly>

      <div class="mt-4">
        <label for="email" class="block font-medium text-sm text-mars-dk pl-2">
          <div class="flex justify-between gap-4"><div>{{ showCompanyForm ? $t('landing.form.workEmail') : $t('landing.form.email') + '*' }}</div>
          <div v-if="form.errorMessagesEmail" class="text-red-700 text-sm" v-html="form.errorMessagesEmail" /></div>
        </label>
        <input id="email" ref="emailInput" v-model="form.email" name="email" type="email" class="form-input rounded-md shadow-sm mt-1 block w-full"
               required autocomplete="work email">
      </div>

      <div class="mt-4">
        <label for="password" class="block font-medium text-sm text-mars-dk pl-2">
        <div class="flex justify-between gap-4"><div>{{ $t('landing.form.password') + '*' }}</div>
          <div v-if="form.errorMessagesPassword" class="text-red-700 text-sm" v-html="form.errorMessagesPassword" />
        </div>
      
        </label>
        <input id="password" v-model="form.password" name="password" type="password" class="form-input rounded-md shadow-sm mt-1 block w-full"
               :class="{'bg-red-200': form.passwordError}"
               required autocomplete="new-password"
               @focusout="checkPassword">
      </div>

      <div class="mt-4">
        <label for="company">
          <div class="flex items-center justify-between text-sm">
            <div>
              {{ $t('landing.form.ascompany') }}
            </div>
            <input id="company" type="checkbox" name="company" value="1" @change="toggleCompanyForm">
          </div>
        </label>
      </div>

      <div v-if="showCompanyForm" class="mt-4">
        <label for="company_name" class="block font-medium text-sm text-mars-dk pl-2">
          <span>{{ $t('landing.form.companyName') + '*' }}</span>
        </label>
        <input id="company_name" class="form-input rounded-md shadow-sm mt-1 block w-full" type="text"
               v-model="form.company_name" name="company_name"
               :required="showCompanyForm" autocomplete="company_name">
      </div>

      <div v-if="showCompanyForm" class="mt-4">
        <label for="vat_id" class="block font-medium text-sm text-mars-dk pl-2">
          <span>{{ $t('landing.form.vatID') }} {{ requiresVAT ? '*' : '' }}</span>
        </label>
        <input id="vat_id" class="form-input rounded-md shadow-sm mt-1 block w-full" type="text"
               v-model="form.vat_id" name="vat_id"
               :required="requiresVAT" autocomplete="vat_id"
               placeholder="DE123456789">
      </div>

      <div v-if="showCompanyForm" class="mt-4">
        <label for="telephone" class="block font-medium text-sm text-mars-dk pl-2">
          <span>{{ $t('landing.form.telephone') + '*' }}</span>
        </label>
        <input id="telephone" v-model="form.telephone" name="telephone" type="input" class="form-input rounded-md shadow-sm mt-1 block w-full" required autocomplete="tel">
      </div>

      <div class="mt-4">
        <label for="terms">
          <div class="flex items-center justify-between text-sm">

            <div><span v-html="$t('landing.form.terms')" />*</div>
            <input id="terms" type="checkbox" v-model="form.terms" name="terms" value="1" required>
          </div>
        </label>
      </div>

      <div class="mt-4">
        <label for="newsletter">
          <div class="flex items-center justify-between text-sm">

            <div v-html="$t('landing.form.newsletter')" />
            <input id="newsletter" type="checkbox" v-model="form.newsletter" name="newsletter" value="1">
          </div>
        </label>
      </div>

      <div class="mt-4">
        <label for="survey">
          <div class="flex items-center justify-between text-sm">

            <div v-html="$t('landing.form.survey')" />
            <input id="survey" type="checkbox" v-model="form.survey" name="survey" value="1">
          </div>
        </label>
      </div>

      <div class="flex items-center justify-end mt-4">
        <span class="text-sm text-lightgray">{{ $t("landing.form.requiredFields") }}</span>
        <div class="flex-grow"></div>
        <a :href="panelLinks.loginUrl.value"
           class="text-md text-gray-600 hover:text-orange cursor-pointer">
          {{ $t('landing.form.login') }}
        </a>
        <button class="ml-4 bg-highlight rounded text-white" :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing">
          {{ $t('landing.form.register') }}
        </button>
      </div>
    </div>
    <input type="hidden" name="terms_version" value="1">
    <input type="hidden" name="registration_landing_mautic_segment_id" :value="form.registration_landing_mautic_segment_id">
  </form>
</template>

<script lang="ts">
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { usePanelLinks } from '~/composables/PanelLinks';

import { countries, countriesIT, type Country } from "~/data/countries"
import { TimeZoneCitiesCountries } from "~/data/tz-cities-to-countries.js"
// import axios from 'axios'

  export default {
    name: 'RegistrationForm',
    inheritAttrs: false,
    customOptions: {}
  }

  export const enum MauticSegmentsID {
    generic = 31
  }
</script>
<script setup lang="ts">

const runtimeConfig = useRuntimeConfig()
const { locale, t} = useI18n()
const panelLinks = usePanelLinks()

// Props
const props = withDefaults(defineProps<{
  mauticSegmentId?: number
  preFormSubmitFunction?: Function
}>(), {
  mauticSegmentId: MauticSegmentsID.generic
})

const registrationForm = ref<HTMLFormElement|null>(null)
const nameInput = ref<HTMLInputElement|null>(null)
const emailInput = ref<HTMLInputElement|null>(null)

const showCompanyForm = ref(false)
const requiresVAT = ref(false)

let name = ''
let email = ''
let password = ''
// debug
if(runtimeConfig.public.debug) {
const now = new Date()
  name = `Test_${now.getFullYear()}.${now.getMonth()+1}.${now.getDate()}_${now.getHours()}.${now.getMinutes()}`
  email = name+'@test.com'
  password = runtimeConfig.public.testPassword
}

const form = ref({
  processing: false,
  survey: false,
  newsletter: false,
  terms: false,
  vat_id: '',
  company_name: '',
  password: password,
  passwordError: false,
  name: name,
  country: null as Country|null ,
  email: email,
  errorMessagesEmail: '',
  errorMessagesPassword: '',
  telephone: '',
  registration_landing_mautic_segment_id: props.mauticSegmentId
})
const panelApiCreateUrl = `${runtimeConfig.public.panelUrl}/api/user/create`

const localizedCountries = computed( ()=> {
  return locale.value === 'it' ? countriesIT : countries
})

const query = ref<string>('')
const filteredCountries = computed<Country[]>( () => {
  return query.value === ''
    ? localizedCountries.value
    : localizedCountries.value.filter( (country) => {
      const qlc = query.value.toLowerCase()
      return country.label.toLowerCase().includes(qlc) || country.code.toLowerCase() === qlc
    })
})

function checkPassword(e: any) { // FocusEvent
  form.value.errorMessagesPassword = ''
  form.value.passwordError = false
  const value = (e.target as HTMLInputElement).value
  if (value === '') {
    return false
  }
  const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/
  if (!strongRegex.test(value)) {
    form.value.errorMessagesPassword = t('landing.check-password').toString()
    form.value.passwordError = true
    return false
  }

  return true
}
// used also externally: see defineExpose() below and page free-demo
function focusInput(el: HTMLInputElement|null = nameInput.value) {
  setTimeout(() => {
    nextTick(() => {
      console.log('focus plz')
      el && el.focus()
    })
  }, 10)
}
function toggleCompanyForm(e: Event) {
    const tgt = e.target as HTMLInputElement
    showCompanyForm.value = tgt.checked
    requiresVAT.value = false // tgt.checked some countries do not have VAT ID. Until we are able to distinguish don't require the field
  }
async function dispatchPost(e: Event) {
  form.value.processing = true
  const formEl = registrationForm.value
  if(!formEl) {
    form.value.processing = false
    return
  }
  if (!checkPassword({ target: { value: (formEl.querySelector('input[type="password"]') as HTMLInputElement).value } })) {
    e.preventDefault()
    form.value.processing = false
    return false
  }

  const data = new URLSearchParams()
  const formData = new FormData(formEl)
  for (const pair of formData) {
    if (typeof pair[1] === 'string') {
      data.append(pair[0], pair[1])
    }
  }
 
  // here it just checks if the user email exists
  // if it pass then the form is submitted
  const response = await fetch(panelApiCreateUrl, {
    method: 'POST',
    body: data
  })
  form.value.errorMessagesEmail = ''
  if (response.status === 409) { // email already taken
    form.value.errorMessagesEmail = t('landing.registration-error', {panelLoginUrl: panelLinks.loginUrl.value}).toString()
    focusInput(emailInput.value)
    e.preventDefault()
    form.value.processing = false
    return false
  } else {
    if(props.preFormSubmitFunction) {
      props.preFormSubmitFunction()
    }
    formEl.submit()
  }
  // could not await form.submit, added some waiting time before re-enabling
  setTimeout( () => form.value.processing = false, 8000)
}

onBeforeMount( () => {
  let userRegion;
  let userCity;
  let userCountry;
  let userTimeZone;

  if (Intl) {
    userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzArr = userTimeZone.split("/");
    userRegion = tzArr[0];
    userCity = tzArr[tzArr.length - 1];
    userCountry = TimeZoneCitiesCountries[userCity];
  }

  console.log("Time Zone:", userTimeZone);
  console.log("Region:", userRegion);
  console.log("City:", userCity);
  console.log("Country:", userCountry);

  // 1. we match againt the english country set
  if(userCountry) {
    const loUserCountry = userCountry.toLowerCase()
    const guessedCountry = countries.find( (country) => {
      return country.label.toLowerCase() === loUserCountry
    })
    // 2. then localize the result
    if(guessedCountry) {
      const localizedCountryMatch = localizedCountries.value.find( (c) => c.code === guessedCountry.code)
      if(localizedCountryMatch) {
        form.value.country = localizedCountryMatch
      }
    }
  }
});

defineExpose({
  focusInput
})
</script>

<style scoped>
* {
  scroll-behavior: smooth
}

input[type=checkbox] {
  @apply text-highlight focus:border-highlight focus:ring-highlight rounded cursor-pointer;
}
</style>
